import { supabase } from '../lib/supabase';
import { emailNotificationService } from './emailNotificationService';

export const leaveService = {
  async getEntitlements(employeeId) {
    const { data, error } = await supabase?.from('leave_entitlements')?.select('*')?.eq('employee_id', employeeId)?.order('year', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      leaveType: row?.leave_type,
      accruedDays: row?.accrued_days,
      usedDays: row?.used_days,
      balanceDays: row?.balance_days,
      year: row?.year,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getAllEntitlements() {
    const { data, error } = await supabase?.from('leave_entitlements')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.eq('year', new Date()?.getFullYear());
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      leaveType: row?.leave_type,
      accruedDays: row?.accrued_days,
      usedDays: row?.used_days,
      balanceDays: row?.balance_days,
      year: row?.year
    })) || [];
  },

  async getRequests(employeeId) {
    const { data, error } = await supabase?.from('leave_requests')?.select('*')?.eq('employee_id', employeeId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      leaveType: row?.leave_type,
      startDate: row?.start_date,
      endDate: row?.end_date,
      daysRequested: row?.days_requested,
      leaveStatus: row?.leave_status,
      reason: row?.reason,
      medicalCertificateRequired: row?.medical_certificate_required,
      medicalCertificateProvided: row?.medical_certificate_provided,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      createdAt: row?.created_at
    })) || [];
  },

  async getAllRequests() {
    const { data, error } = await supabase?.from('leave_requests')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      leaveType: row?.leave_type,
      startDate: row?.start_date,
      endDate: row?.end_date,
      daysRequested: row?.days_requested,
      leaveStatus: row?.leave_status,
      reason: row?.reason,
      medicalCertificateRequired: row?.medical_certificate_required,
      medicalCertificateProvided: row?.medical_certificate_provided,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      createdAt: row?.created_at
    })) || [];
  },

  async createRequest(request) {
    const { data, error } = await supabase?.from('leave_requests')?.insert({
        employee_id: request?.employeeId,
        leave_type: request?.leaveType,
        start_date: request?.startDate,
        end_date: request?.endDate,
        days_requested: request?.daysRequested,
        reason: request?.reason,
        medical_certificate_required: request?.medicalCertificateRequired || false
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeId: data?.employee_id,
      leaveType: data?.leave_type,
      startDate: data?.start_date,
      endDate: data?.end_date,
      daysRequested: data?.days_requested,
      leaveStatus: data?.leave_status,
      createdAt: data?.created_at
    };
  },

  async updateRequest(id, request) {
    const { data, error } = await supabase?.from('leave_requests')?.update({
        leave_status: request?.leaveStatus,
        approved_by: request?.approvedBy,
        approved_at: request?.approvedAt,
        medical_certificate_provided: request?.medicalCertificateProvided
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      leaveStatus: data?.leave_status,
      approvedBy: data?.approved_by,
      approvedAt: data?.approved_at
    };
  },

  async approveRequest(requestId, approvedBy) {
    try {
      // Get leave request details with employee info
      const { data: leaveRequest, error: fetchError } = await supabase
        ?.from('leave_requests')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email,
            department
          )
        `)
        ?.eq('id', requestId)
        ?.single();

      if (fetchError) throw fetchError;
      if (!leaveRequest) throw new Error('Leave request not found');

      const { data, error } = await supabase
        ?.from('leave_requests')
        ?.update({
          leave_status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date()?.toISOString()
        })
        ?.eq('id', requestId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Send email notification to employee
      if (leaveRequest?.employees?.email) {
        try {
          await emailNotificationService?.sendLeaveApprovalAlert(
            leaveRequest?.employees?.email,
            {
              id: leaveRequest?.id,
              employeeId: leaveRequest?.employee_id,
              employeeName: `${leaveRequest?.employees?.first_name || ''} ${leaveRequest?.employees?.last_name || ''}`?.trim(),
              leaveType: leaveRequest?.leave_type,
              startDate: leaveRequest?.start_date,
              endDate: leaveRequest?.end_date,
              daysRequested: leaveRequest?.days_requested,
              approvedBy: approvedBy,
              approvedAt: data?.approved_at
            },
            'approved'
          );
        } catch (emailError) {
          console.error('Failed to send leave approval email:', emailError);
          // Don't throw error - leave approval should succeed even if email fails
        }
      }

      return {
        id: data?.id,
        employeeId: data?.employee_id,
        leaveType: data?.leave_type,
        startDate: data?.start_date,
        endDate: data?.end_date,
        daysRequested: data?.days_requested,
        leaveStatus: data?.leave_status,
        reason: data?.reason,
        approvedBy: data?.approved_by,
        approvedAt: data?.approved_at,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  },

  async rejectRequest(requestId, rejectedBy, rejectionReason) {
    try {
      // Get leave request details with employee info
      const { data: leaveRequest, error: fetchError } = await supabase
        ?.from('leave_requests')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email,
            department
          )
        `)
        ?.eq('id', requestId)
        ?.single();

      if (fetchError) throw fetchError;
      if (!leaveRequest) throw new Error('Leave request not found');

      const { data, error } = await supabase
        ?.from('leave_requests')
        ?.update({
          leave_status: 'rejected',
          approved_by: rejectedBy,
          approved_at: new Date()?.toISOString()
        })
        ?.eq('id', requestId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Send email notification to employee
      if (leaveRequest?.employees?.email) {
        try {
          await emailNotificationService?.sendLeaveApprovalAlert(
            leaveRequest?.employees?.email,
            {
              id: leaveRequest?.id,
              employeeId: leaveRequest?.employee_id,
              employeeName: `${leaveRequest?.employees?.first_name || ''} ${leaveRequest?.employees?.last_name || ''}`?.trim(),
              leaveType: leaveRequest?.leave_type,
              startDate: leaveRequest?.start_date,
              endDate: leaveRequest?.end_date,
              daysRequested: leaveRequest?.days_requested,
              approvedBy: rejectedBy,
              approvedAt: data?.approved_at,
              rejectionReason: rejectionReason || 'No reason provided'
            },
            'rejected'
          );
        } catch (emailError) {
          console.error('Failed to send leave rejection email:', emailError);
          // Don't throw error - leave rejection should succeed even if email fails
        }
      }

      return {
        id: data?.id,
        employeeId: data?.employee_id,
        leaveType: data?.leave_type,
        startDate: data?.start_date,
        endDate: data?.end_date,
        daysRequested: data?.days_requested,
        leaveStatus: data?.leave_status,
        reason: data?.reason,
        approvedBy: data?.approved_by,
        approvedAt: data?.approved_at,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  },

  async getPendingRequests() {
    const { data, error } = await supabase?.from('leave_requests')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.eq('leave_status', 'pending')?.order('created_at', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      leaveType: row?.leave_type,
      startDate: row?.start_date,
      endDate: row?.end_date,
      daysRequested: row?.days_requested,
      reason: row?.reason,
      createdAt: row?.created_at
    })) || [];
  },

  async getBalanceAlerts() {
    const { data, error } = await supabase?.from('leave_entitlements')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.eq('year', new Date()?.getFullYear())?.gte('balance_days', 20);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      leaveType: row?.leave_type,
      balanceDays: row?.balance_days,
      accruedDays: row?.accrued_days,
      usedDays: row?.used_days
    })) || [];
  }
};

export default leaveService;